/**
 * Trust-check log entry support for CallLogs.
 *
 * Contract (matches docs/specs/non-meeting-trust-check-affidavits.md § 4 and § 7):
 *
 * @typedef {Object} TrustCheckLogEntry
 * @property {string} trust_check_session_id
 * @property {('servicenow'|'sdk'|'adhoc'|'invite'|'dynamic'|'zoom_login')} link_type
 * @property {string} link_title                           // the link's display title
 * @property {string} app_id
 * @property {string} app_name                             // app display name (source column)
 * @property {string} account_id
 * @property {string} poly_user_id
 * @property {string} verified_subject_name
 * @property {string} completed_at                          // ISO-8601
 * @property {number|null} biometric_confidence            // 0-100
 * @property {('PASS'|'WARN'|'FAIL'|'INCOMPLETE'|'NOT_ENABLED'|'OFFLINE'|null)} presence_score
 * @property {string|null} affidavit_key                   // null => PDF unavailable; row still rendered
 *
 * Display-side row shape (after normalization, shared with meeting rows so the
 * existing table can iterate one mixed array):
 *
 * @typedef {Object} TrustCheckLogRow
 * @property {string} log_id           // unique row id; derived from trust_check_session_id
 * @property {'trust_check'} callType
 * @property {string} timestamp        // ISO-8601, used by sort/format
 * @property {string} title            // verified_subject_name (used by existing Title/Host column)
 * @property {string} link_type
 * @property {string} app_id
 * @property {string} account_id
 * @property {string} poly_user_id
 * @property {string} verified_subject_name
 * @property {number|null} biometric_confidence
 * @property {string|null} presence_score
 * @property {string|null} affidavit_key
 * @property {string} trust_check_session_id
 */

/**
 * Translation keys for the six non-meeting link types. Returns the human-readable
 * kind label for a given link_type plus a deterministic MUI color string.
 *
 * Colors are picked from MUI's standard palette tokens so they respect theme.
 */
export const LINK_TYPE_LABELS = {
    servicenow: { key: 'callLogs.linkTypeServiceNow', color: 'info' },
    sdk: { key: 'callLogs.linkTypeSDK', color: 'primary' },
    adhoc: { key: 'callLogs.linkTypeAdhoc', color: 'warning' },
    invite: { key: 'callLogs.linkTypeInvite', color: 'success' },
    dynamic: { key: 'callLogs.linkTypeDynamic', color: 'secondary' },
    zoom_login: { key: 'callLogs.linkTypeZoomLogin', color: 'default' },
};

export const PRESENCE_SCORE_COLORS = {
    PASS: 'success',
    WARN: 'warning',
    FAIL: 'error',
    INCOMPLETE: 'default',
    NOT_ENABLED: 'default',
    OFFLINE: 'default',
};

/**
 * Look up the i18n label key + MUI color for a given trust-check link_type.
 * Falls back to a neutral chip if an unknown link_type slips through (e.g. a
 * new link type added on the backend before the frontend learns about it).
 */
export function getLinkTypeLabel(linkType) {
    return LINK_TYPE_LABELS[linkType] || { key: 'callLogs.unknown', color: 'default' };
}

export function getPresenceScoreColor(score) {
    if (!score) return 'default';
    return PRESENCE_SCORE_COLORS[score] || 'default';
}

/**
 * Format biometric confidence for display. Accepts the 0-100 backend value
 * and returns a string like "98.5%". Returns "-" for null / undefined.
 */
export function formatBiometricConfidence(confidence) {
    if (confidence === null || confidence === undefined) return '-';
    const n = Number(confidence);
    if (Number.isNaN(n)) return '-';
    return `${n.toFixed(1)}%`;
}

/**
 * Normalize a backend TrustCheckLogEntry into a row shape the table can iterate
 * uniformly alongside meetings. The `log_id` is derived from the session id so
 * `/activity/log/:logId` lookups resolve correctly.
 *
 * @param {TrustCheckLogEntry} entry
 * @param {number} index
 * @returns {TrustCheckLogRow}
 */
export function normalizeTrustCheckEntry(entry, index) {
    // SDK links default to a generic "Secure Meeting" title that doesn't
    // tell the operator which app produced the verification. The app name
    // is more informative, so prefer it for the Title column on SDK rows.
    const isSdk = entry.link_type === 'sdk';
    const title = isSdk
        ? (entry.app_name || entry.link_title || entry.verified_subject_name || '')
        : (entry.link_title || entry.verified_subject_name || '');
    return {
        log_id: `tc-${entry.trust_check_session_id}`,
        callType: 'trust_check',
        timestamp: entry.completed_at,
        title,
        link_type: entry.link_type,
        app_id: entry.app_id,
        app_name: entry.app_name,
        account_id: entry.account_id,
        poly_user_id: entry.poly_user_id,
        verified_subject_name: entry.verified_subject_name,
        biometric_confidence: entry.biometric_confidence,
        presence_score: entry.presence_score,
        affidavit_key: entry.affidavit_key,
        trust_check_session_id: entry.trust_check_session_id,
        _orderHint: index,
    };
}
